// src/pages/Onboarding/slides/BeforeAfterSlide.js
// 
// VISUAL STRUCTURE: Same as ConversionSlide (uses SlideShell, dark theme, sequential reveals)
// BACKEND LOGIC: Same as ImageGenSlide (hairgen job consumption, Firestore sync, tracking)

import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import SlideShell from '../SlideShell';
import { getImageUploadArtifact, setImageUploadArtifact } from '../../../utils/Onboarding/artifacts';
import { trackEvent } from '../../../utils/Tracking';
import { trackMetaLead } from '../../../utils/metaPixel';
import { trackTikTokContact } from '../../../utils/tikTokPixel';
import BeforeAfterSlider from '../../../components/Onboarding/BeforeAfterSlider';
import BenefitsDisplay from '../../../components/Onboarding/BenefitsDisplay';
import SaveReportPopup from '../../../components/Popup/SaveReportPopup';
import WhatsAppLogo from '../../../images/WhatsAppLogo.png';
import { sendGenerationSMS } from '../../../utils/Onboarding/smsApi';
import { sendGenerationEmail } from '../../../utils/Onboarding/autoMail';
import { 
  waitForHairgenJob, 
  isJobForInput,
} from '../../../utils/Onboarding/hairgenJobTracker';
import { 
  getGeneratedImageUrl, 
  getProcessingTime, 
} from '../../../utils/Onboarding/hairgenApi';

// ========================================
// TIMING CONFIGURATION
// ========================================
const MINIMUM_LOADING_TIME_MS = 5000; // Minimum 5 seconds loading for UX

// ========================================
// PROGRESS BAR ANIMATION HOOK (same as ConversionSlide)
// ========================================
function useAnimatedPercent(targetPercent, durationMs = 900, delayMs = 50, shouldStart = true) {
  const [value, setValue] = useState(0);
  
  useEffect(() => {
    if (!shouldStart) {
      setValue(0);
      return;
    }
    
    let raf;
    const start = () => {
      const t0 = performance.now();
      const run = (ts) => {
        const elapsed = ts - t0;
        const p = Math.min(1, elapsed / durationMs);
        // easeOutCubic
        const eased = 1 - Math.pow(1 - p, 3);
        setValue(targetPercent * eased);
        if (p < 1) raf = requestAnimationFrame(run);
      };
      raf = requestAnimationFrame(run);
    };
    const id = setTimeout(start, Math.max(0, delayMs));
    return () => {
      clearTimeout(id);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [targetPercent, durationMs, delayMs, shouldStart]);
  
  return value;
}

// ========================================
// PROGRESS BAR COMPONENT (same style as ConversionSlide)
// ========================================
function ProgressBar({ percent, label, minLabel = 'Low', maxLabel = 'High', color = 'bg-white', trackColor = 'bg-white/30', labelTextColor = '#111827', startAnimation = true }) {
  const animated = useAnimatedPercent(startAnimation ? percent : 0, 900, 80);
  const widthStyle = { width: `${Math.max(0, Math.min(100, animated)).toFixed(1)}%` };
  return (
    <div className="w-full">
      <div className={`relative w-full h-6 rounded-full ${trackColor}`}> 
        <div className={`absolute inset-y-0 left-0 rounded-full ${color} flex items-center overflow-hidden`} style={widthStyle}>
          {label && (
            <span className="ml-2 inline-flex items-center rounded-full bg-white/90 px-2 py-0.5 text-xs font-semibold shadow-sm whitespace-nowrap" style={{ color: labelTextColor }}>
              {label}
            </span>
          )}
        </div>
      </div>
      <div className="mt-2 flex w-full justify-between text-xs text-white/90">
        <span>{minLabel}</span>
        <span>{maxLabel}</span>
      </div>
    </div>
  );
}

// ========================================
// GRADIENT CARD COLORS (same as ConversionSlide)
// ========================================
const CARD_GRADIENTS = {
  candidateScore: {
    from: '#41537C',
    to: '#6387DB',
  },
  expectedSatisfaction: {
    from: '#AC3FBC',
    to: '#E570F7',
  },
};

// ========================================
// COLORED CARD COMPONENT (same style as ConversionSlide)
// ========================================
function ColoredCard({ title, subtitle, gradientColors, children }) {
  return (
    <div 
      className="rounded-2xl p-4 sm:p-5 shadow-lg text-white"
      style={{
        background: `linear-gradient(to bottom right, ${gradientColors.from}, ${gradientColors.to})`
      }}
    > 
      <div className="mb-3">
        <h3 className="text-base sm:text-lg font-semibold leading-snug">{title}</h3>
        {subtitle && (
          <p className="mt-1 text-xs sm:text-sm text-white/90">{subtitle}</p>
        )}
      </div>
      {children}
    </div>
  );
}

// ========================================
// LOADING DISPLAY COMPONENT
// ========================================
function LoadingDisplay({ loadingText, loadingProgress, loadingTextVisible }) {
  return (
    <div 
      className="flex flex-col items-center justify-center w-full px-6"
      style={{ minHeight: 'calc(100vh - 200px)' }}
    >
      {/* Progress bar - vertically static */}
      <div className="w-full max-w-xs">
        <div className="w-full h-2 rounded-full bg-white/30 relative overflow-hidden">
          <div 
            className="absolute top-0 left-0 h-full rounded-full bg-white transition-all duration-300 ease-out"
            style={{ width: `${loadingProgress}%` }}
          />
        </div>
        <p className="text-center text-white/60 text-sm mt-3 tabular-nums">
          {Math.round(loadingProgress)}%
        </p>
      </div>
      
      {/* Loading text - fixed height container to prevent layout shift */}
      <div className="h-16 flex items-center justify-center mt-6 w-full">
        <p 
          className="text-white/90 text-base text-center px-6 transition-opacity duration-300 leading-relaxed"
          style={{ opacity: loadingTextVisible ? 1 : 0 }}
        >
          {loadingText}
        </p>
      </div>
    </div>
  );
}

/**
 * BEFORE/AFTER SLIDE
 * 
 * Visual structure: Same as ConversionSlide (SlideShell, dark theme, sequential reveals)
 * Backend logic: Same as ImageGenSlide (hairgen job consumption, Firestore sync, tracking)
 * 
 * RESPONSIBILITIES:
 * - Consume hairgen job from background (started in UploadSlide)
 * - Display before/after comparison slider
 * - Show Hair Transplant Score and Hairline analysis cards
 * - Save results to artifacts for ConversionSlide
 * - Track hairgen completion event
 * - Sync results to Firestore
 */
export default function BeforeAfterSlide({
  currentStep,
  totalSteps,
  onNext,
  onBack,
  onReset = null,
  onAutoSave = null,
  theme = 'dark',
  sessionId = null,
  userName = '',
  animationDirection = 'forward',
  isTransitioning = false,
  // Dynamic values from OnboardingPage controller
  candidateScorePercent = 72,
  expectedSatisfactionPercent = 92,
}) {
  // ========================================
  // IMAGE STATE
  // ========================================
  const [imageDataUrl, setImageDataUrl] = useState(null);
  const [generatedImageUrl, setGeneratedImageUrl] = useState(null);
  const [firebaseStoragePath, setFirebaseStoragePath] = useState(null);
  
  // ========================================
  // ANALYSIS STATE: LOADING -> FINAL
  // ========================================
  const [analysisState, setAnalysisState] = useState('LOADING');
  const [generationError, setGenerationError] = useState(null);
  const [processingTime, setProcessingTime] = useState(null);
  
  // Job consumption flag (prevent duplicate processing)
  const jobConsumedRef = useRef(false);
  // Track when we started loading (for minimum wait time)
  const loadingStartTimeRef = useRef(null);
  
  // ========================================
  // LOADING PROGRESS STATE
  // ========================================
  const [loadingProgress, setLoadingProgress] = useState(0);
  
  // Loading text rotation
  const LOADING_TEXTS = useMemo(() => [
    'Simulating your hair transplant results',
    'Matching with 1000+ previous patients',
    'Simulating graft placement and density',
    'Calculating follicle survival rate',
    'Reality check with reference data',
  ], []);
  const [loadingTextIndex, setLoadingTextIndex] = useState(0);
  const [loadingTextVisible, setLoadingTextVisible] = useState(true);
  
  // ========================================
  // SEQUENTIAL REVEAL ANIMATION STATE
  // ========================================
  const [showBeforeAfterHeader, setShowBeforeAfterHeader] = useState(false);
  const [beforeAfterHeaderComplete, setBeforeAfterHeaderComplete] = useState(false);
  const [showBeforeAfterSlider, setShowBeforeAfterSlider] = useState(false);
  const [beforeAfterSliderComplete, setBeforeAfterSliderComplete] = useState(false);
  const [showScoreCard, setShowScoreCard] = useState(false);
  const [scoreCardComplete, setScoreCardComplete] = useState(false);
  const [showSatisfactionCard, setShowSatisfactionCard] = useState(false);
  const [satisfactionCardComplete, setSatisfactionCardComplete] = useState(false);
  const [showWhatsAppCard, setShowWhatsAppCard] = useState(false);
  const [whatsAppCardComplete, setWhatsAppCardComplete] = useState(false);
  const [showBenefits, setShowBenefits] = useState(false);
  const [benefitsComplete, setBenefitsComplete] = useState(false);
  
  // ========================================
  // SAVE REPORT POPUP STATE
  // ========================================
  const [showSaveReportPopup, setShowSaveReportPopup] = useState(false);
  const [isReportSaved, setIsReportSaved] = useState(false);
  const [showSaveHighlight, setShowSaveHighlight] = useState(false);
  
  // ========================================
  // SMS CREDENTIALS (uses ANALYSIS requestId, not generation)
  // ========================================
  const [smsAnalysisRequestId, setSmsAnalysisRequestId] = useState(null);
  const [smsPassword, setSmsPassword] = useState(null);
  
  // ========================================
  // PRIMARY BUTTON VISIBILITY
  // ========================================
  const [showPrimaryButton, setShowPrimaryButton] = useState(false);
  
  // ========================================
  // COMPUTED VALUES
  // ========================================
  
  // Format display name
  const displayFirstName = useMemo(() => {
    if (!userName) return '';
    const firstName = userName.trim().split(/\s+/)[0] || '';
    if (!firstName) return '';
    return firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase();
  }, [userName]);
  
  // Check if we have both images
  const hasComparisonImages = !!(imageDataUrl && generatedImageUrl);
  
  // ========================================
  // EFFECT: LOAD ARTIFACTS ON MOUNT
  // ========================================
  useEffect(() => {
    if (!sessionId) {
      console.warn('[BeforeAfterSlide] No sessionId provided');
      return;
    }
    
    const artifact = getImageUploadArtifact(sessionId);
    
    if (!artifact || !artifact.imageDataUrl) {
      console.warn('[BeforeAfterSlide] No image found in artifacts');
      return;
    }
    
    setImageDataUrl(artifact.imageDataUrl);
    
    if (artifact.firebaseStoragePath) {
      setFirebaseStoragePath(artifact.firebaseStoragePath);
    }
    
    // Check if we have a cached hairgen result (user returned via back button)
    // Still show loading animation, but with cached result ready
    if (artifact.hairgenResultUrl) {
      setGeneratedImageUrl(artifact.hairgenResultUrl);
      
      if (artifact.hairgenProcessingTimeMs) {
        setProcessingTime(artifact.hairgenProcessingTimeMs);
      }
      
      // Mark as consumed but keep LOADING state - animation effect will transition to FINAL
      jobConsumedRef.current = true;
    }
  }, [sessionId]);
  
  // ========================================
  // PROCESS JOB RESULT CALLBACK
  // ========================================
  const processJobResult = useCallback((result, inputPath) => {
    const generatedUrl = getGeneratedImageUrl(result);
    const procTime = getProcessingTime(result);
    
    // Extract credentials for SMS functionality
    // API responses are wrapped in 'operation' property
    // IMPORTANT: Use ANALYSIS requestId (from reportMetadata), not generation requestId
    // The SMS link should go to the analysis report with ?tab=generation
    const operation = result?.operation || result;
    const analysisReqId = operation?.reportMetadata?.analysisRequestId || null;
    const reqPassword = operation?.firestore?.password || null;
    
    console.log('[SMS System] processJobResult - extracting credentials for SMS:', {
      analysisRequestId: analysisReqId,
      generationRequestId: operation?.requestId, // Just for logging
      hasPassword: !!reqPassword,
      hasOperation: !!result?.operation,
      reportMetadataKeys: operation?.reportMetadata ? Object.keys(operation.reportMetadata) : null,
      firestoreKeys: operation?.firestore ? Object.keys(operation.firestore) : null,
    });
    
    if (analysisReqId) {
      setSmsAnalysisRequestId(analysisReqId);
    } else {
      console.warn('[SMS System] No analysisRequestId found in reportMetadata - SMS will not work');
    }
    if (reqPassword) {
      setSmsPassword(reqPassword);
    }
    
    if (generatedUrl) {
      // Preload the generated image before displaying
      const preloadImage = new Image();
      preloadImage.onload = () => {
        setGeneratedImageUrl(generatedUrl);
      };
      preloadImage.onerror = () => {
        console.error('[BeforeAfterSlide] Failed to preload generated image');
        setGeneratedImageUrl(generatedUrl);
      };
      preloadImage.src = generatedUrl;
      
      if (procTime) {
        setProcessingTime(procTime);
      }
      
      // Save to artifacts for later (back button caching + ConversionSlide access)
      if (sessionId) {
        setImageUploadArtifact(sessionId, {
          hairgenResultUrl: generatedUrl,
          hairgenProcessingTimeMs: procTime,
          hairgenTimestamp: new Date().toISOString(),
        });
      }
      
      // Auto-save to Firestore
      if (onAutoSave) {
        const hairgenSummary = {
          generatedStorageUrl: generatedUrl,
          inputStoragePath: inputPath,
          processingTimeMs: procTime,
          timestamp: new Date().toISOString(),
        };
        
        onAutoSave({
          selfieHairgenResults: [hairgenSummary],
          lastHairgenGeneratedUrl: generatedUrl,
          lastHairgenInputPath: inputPath,
          lastHairgenProcessingTimeMs: procTime,
        });
      }
      
      // Track successful completion
      trackEvent('onboarding_checkpoint_hairgen_completed', {
        onboarding_id: sessionId || null,
        had_error: false,
        generated_storage_url: generatedUrl,
        input_storage_path: inputPath,
        processing_time_ms: procTime,
        wait_time_on_slide_ms: loadingStartTimeRef.current 
          ? Date.now() - loadingStartTimeRef.current 
          : null,
      }).catch(console.error);
      
      return true;
    }
    
    return false;
  }, [sessionId, onAutoSave]);
  
  // ========================================
  // EFFECT: CONSUME HAIRGEN JOB
  // ========================================
  useEffect(() => {
    if (!firebaseStoragePath || jobConsumedRef.current) {
      return;
    }
    
    // Mark loading start time
    loadingStartTimeRef.current = Date.now();
    
    const consumeJob = async () => {
      // Verify the job is for our current input image
      if (!isJobForInput(firebaseStoragePath)) {
        console.warn('[BeforeAfterSlide] No matching job found for:', firebaseStoragePath);
        setGenerationError('No hair generation job found');
        setLoadingProgress(100);
        setAnalysisState('FINAL');
        jobConsumedRef.current = true;
        return;
      }
      
      try {
        // Wait for the job to complete
        const finalStatus = await waitForHairgenJob();
        
        // Calculate time spent on this slide
        const timeOnSlide = Date.now() - loadingStartTimeRef.current;
        
        // Enforce minimum loading time for UX
        const remainingWait = MINIMUM_LOADING_TIME_MS - timeOnSlide;
        if (remainingWait > 0) {
          await new Promise(resolve => setTimeout(resolve, remainingWait));
        }
        
        // Set progress to 100%
        setLoadingProgress(100);
        
        // Process the result
        if (finalStatus.status === 'success' && finalStatus.result) {
          const processed = processJobResult(finalStatus.result, firebaseStoragePath);
          if (!processed) {
            console.warn('[BeforeAfterSlide] Could not extract generated URL from result');
            setGenerationError('Invalid result format');
          }
        } else if (finalStatus.status === 'error') {
          console.error('[BeforeAfterSlide] Job failed:', finalStatus.error);
          setGenerationError(finalStatus.error || 'Hair generation failed');
          
          // Track error
          trackEvent('onboarding_checkpoint_hairgen_completed', {
            onboarding_id: sessionId || null,
            had_error: true,
            error_message: finalStatus.error,
            input_storage_path: firebaseStoragePath,
            wait_time_on_slide_ms: Date.now() - loadingStartTimeRef.current,
          }).catch(console.error);
        } else {
          console.warn('[BeforeAfterSlide] Unexpected job status:', finalStatus.status);
          setGenerationError('Unexpected job state');
        }
      } catch (err) {
        console.error('[BeforeAfterSlide] Error consuming job:', err);
        setGenerationError(err.message || 'Failed to get result');
      } finally {
        // Always transition to FINAL state
        jobConsumedRef.current = true;
        setAnalysisState('FINAL');
      }
    };
    
    consumeJob();
  }, [firebaseStoragePath, sessionId, processJobResult]);
  
  // ========================================
  // EFFECT: LOADING PROGRESS ANIMATION
  // ========================================
  useEffect(() => {
    if (analysisState !== 'LOADING') return;
    
    // Reset progress when entering loading state
    setLoadingProgress(0);
    
    // Animate progress from 0 to 90% over the minimum loading time
    const startTime = Date.now();
    const targetProgress = 90; // Leave room for completion
    
    const intervalId = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(targetProgress, (elapsed / MINIMUM_LOADING_TIME_MS) * targetProgress);
      setLoadingProgress(progress);
      
      if (progress >= targetProgress) {
        clearInterval(intervalId);
      }
    }, 50); // Update every 50ms for smooth animation
    
    return () => clearInterval(intervalId);
  }, [analysisState]);
  
  // ========================================
  // EFFECT: TRANSITION TO FINAL WHEN CACHED RESULTS READY
  // ========================================
  useEffect(() => {
    if (analysisState !== 'LOADING') return;
    if (!generatedImageUrl) return; // No cached result yet
    
    // We have cached results - wait for minimum loading time then transition
    const timer = setTimeout(() => {
      setLoadingProgress(100);
      // Small delay after reaching 100% before transitioning
      setTimeout(() => {
        setAnalysisState('FINAL');
      }, 300);
    }, MINIMUM_LOADING_TIME_MS);
    
    return () => clearTimeout(timer);
  }, [analysisState, generatedImageUrl]);
  
  // ========================================
  // EFFECT: LOADING TEXT ROTATION
  // ========================================
  useEffect(() => {
    if (analysisState !== 'LOADING') return;
    
    const DISPLAY_TIME = 2000;
    const FADE_TIME = 300;
    
    const intervalId = setInterval(() => {
      setLoadingTextVisible(false);
      
      setTimeout(() => {
        setLoadingTextIndex(prev => (prev + 1) % LOADING_TEXTS.length);
        setLoadingTextVisible(true);
      }, FADE_TIME);
    }, DISPLAY_TIME);
    
    return () => clearInterval(intervalId);
  }, [analysisState, LOADING_TEXTS.length]);
  
  // ========================================
  // EFFECT: START SEQUENTIAL REVEALS WHEN FINAL
  // ========================================
  useEffect(() => {
    if (analysisState !== 'FINAL') return;
    
    // Start showing before/after header after a short delay
    const timer = setTimeout(() => {
      setShowBeforeAfterHeader(true);
    }, 300);
    
    return () => clearTimeout(timer);
  }, [analysisState]);
  
  // ========================================
  // EFFECT: CHAIN ANIMATIONS
  // ========================================
  
  // After header completes, show slider
  useEffect(() => {
    if (beforeAfterHeaderComplete && !showBeforeAfterSlider) {
      const timer = setTimeout(() => setShowBeforeAfterSlider(true), 200);
      return () => clearTimeout(timer);
    }
  }, [beforeAfterHeaderComplete, showBeforeAfterSlider]);
  
  // After slider completes, show score card
  useEffect(() => {
    if (beforeAfterSliderComplete && !showScoreCard) {
      const timer = setTimeout(() => setShowScoreCard(true), 300);
      return () => clearTimeout(timer);
    }
  }, [beforeAfterSliderComplete, showScoreCard]);
  
  // After score card completes, show satisfaction card
  useEffect(() => {
    if (scoreCardComplete && !showSatisfactionCard) {
      const timer = setTimeout(() => setShowSatisfactionCard(true), 200);
      return () => clearTimeout(timer);
    }
  }, [scoreCardComplete, showSatisfactionCard]);
  
  // After satisfaction card completes, show WhatsApp card
  useEffect(() => {
    if (satisfactionCardComplete && !showWhatsAppCard) {
      const timer = setTimeout(() => setShowWhatsAppCard(true), 200);
      return () => clearTimeout(timer);
    }
  }, [satisfactionCardComplete, showWhatsAppCard]);
  
  // After WhatsApp card completes, show benefits display
  useEffect(() => {
    if (whatsAppCardComplete && !showBenefits) {
      const timer = setTimeout(() => setShowBenefits(true), 200);
      return () => clearTimeout(timer);
    }
  }, [whatsAppCardComplete, showBenefits]);
  
  // After benefits complete, show primary button
  useEffect(() => {
    if (benefitsComplete && !showPrimaryButton) {
      const timer = setTimeout(() => setShowPrimaryButton(true), 200);
      return () => clearTimeout(timer);
    }
  }, [benefitsComplete, showPrimaryButton]);
  
  // ========================================
  // SKIP HANDLER: ADVANCE THROUGH ANIMATIONS
  // ========================================
  const handleSkipAdvance = useCallback(() => {
    if (analysisState !== 'FINAL') return;
    
    if (!showBeforeAfterHeader) {
      setShowBeforeAfterHeader(true);
      return;
    }
    if (!beforeAfterHeaderComplete) {
      setBeforeAfterHeaderComplete(true);
      return;
    }
    if (!showBeforeAfterSlider) {
      setShowBeforeAfterSlider(true);
      return;
    }
    if (!beforeAfterSliderComplete) {
      setBeforeAfterSliderComplete(true);
      return;
    }
    if (!showScoreCard) {
      setShowScoreCard(true);
      return;
    }
    if (!scoreCardComplete) {
      setScoreCardComplete(true);
      return;
    }
    if (!showSatisfactionCard) {
      setShowSatisfactionCard(true);
      return;
    }
    if (!satisfactionCardComplete) {
      setSatisfactionCardComplete(true);
      return;
    }
    if (!showWhatsAppCard) {
      setShowWhatsAppCard(true);
      return;
    }
    if (!whatsAppCardComplete) {
      setWhatsAppCardComplete(true);
      return;
    }
    if (!showBenefits) {
      setShowBenefits(true);
      return;
    }
    if (!benefitsComplete) {
      setBenefitsComplete(true);
      return;
    }
    if (!showPrimaryButton) {
      setShowPrimaryButton(true);
    }
  }, [analysisState, showBeforeAfterHeader, beforeAfterHeaderComplete, showBeforeAfterSlider, beforeAfterSliderComplete, showScoreCard, scoreCardComplete, showSatisfactionCard, satisfactionCardComplete, showWhatsAppCard, whatsAppCardComplete, showBenefits, benefitsComplete, showPrimaryButton]);
  
  // Touch handlers for skip (only in FINAL state)
  const touchHandlers = analysisState === 'FINAL' ? {
    onPointerDown: handleSkipAdvance,
    onClick: handleSkipAdvance,
    onTouchStart: handleSkipAdvance,
  } : {};
  
  // ========================================
  // CUSTOM FOOTER WITH SAVE + CONTINUE BUTTONS
  // ========================================
  const customFooter = showPrimaryButton ? (
    <div className="w-full px-5 sm:px-6 py-4">
      <div className="flex gap-3">
        {/* Save Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (!isReportSaved) {
              setShowSaveReportPopup(true);
            }
          }}
          disabled={isReportSaved}
          className={`py-3 px-5 text-base font-medium rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
            isReportSaved 
              ? 'cursor-default bg-gray-700 text-gray-400 border border-gray-600' 
              : 'hover:bg-white/10 text-white border border-white/50'
          } ${showSaveHighlight ? 'save-highlight-animation' : ''}`}
          onAnimationEnd={() => setShowSaveHighlight(false)}
        >
          {isReportSaved ? 'Saved' : 'Save'}
        </button>
        
        {/* Continue Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNext && onNext(null, {});
          }}
          className="flex-1 py-3 px-6 text-base font-bold rounded-full bg-white text-gray-900 hover:bg-gray-100 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2"
        >
          Continue
        </button>
      </div>
    </div>
  ) : null;
  
  // ========================================
  // RENDER
  // ========================================
  return (
    <div {...touchHandlers}>
      <SlideShell
        title={null}
        subtitle={null}
        currentStep={currentStep}
        totalSteps={totalSteps}
        onNext={onNext}
        onBack={onBack}
        nextLabel="Continue"
        theme={theme}
        animationDirection={animationDirection}
        isTransitioning={isTransitioning}
        showPrimary={false}
        footer={customFooter}
        scrollable
        contentJustify="start"
        contentClassName="no-scrollbar-desktop"
        animateScrollableChildrenOnly
        hideBottomSpacer={true}
      >
        <style>{`
          @keyframes cardFadeIn {
            0% { opacity: 0; transform: translateY(8px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          .card-reveal {
            opacity: 0;
            animation: cardFadeIn 400ms cubic-bezier(0.22, 1, 0.36, 1) forwards;
          }
          @media (prefers-reduced-motion: reduce) {
            .card-reveal { animation: cardFadeIn 1ms linear forwards !important; }
          }
          .no-scrollbar-desktop::-webkit-scrollbar { display: none; }
          .no-scrollbar-desktop { scrollbar-width: none; -ms-overflow-style: none; }
          
          @keyframes saveHighlight {
            0% {
              box-shadow: 0 0 0 0 rgba(34, 197, 94, 0);
              border-color: #4b5563;
            }
            30% {
              box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.6);
              border-color: rgb(34, 197, 94);
            }
            100% {
              box-shadow: 0 0 0 0 rgba(34, 197, 94, 0);
              border-color: #4b5563;
            }
          }
          .save-highlight-animation {
            animation: saveHighlight 2s ease-out forwards;
          }
        `}</style>

        {/* LOADING STATE - absolute positioned for true centering */}
        {analysisState === 'LOADING' && (
          <LoadingDisplay 
            loadingText={LOADING_TEXTS[loadingTextIndex]}
            loadingProgress={loadingProgress}
            loadingTextVisible={loadingTextVisible}
          />
        )}

        {/* FINAL STATE - Sequential reveals */}
        {analysisState === 'FINAL' && (
        <div className="mx-auto w-full max-w-md">
          <div className="space-y-6">
              {/* Before/After Section */}
              {hasComparisonImages && showBeforeAfterHeader && (
                <div>
                  {/* Header */}
                  <div 
                    className="card-reveal mb-4"
                    onAnimationEnd={() => setBeforeAfterHeaderComplete(true)}
                  >
                    <h1 className="text-white text-2xl font-bold leading-tight mb-2">
                      {displayFirstName ? `${displayFirstName}, you are a strong candidate for a hair transplant` : 'You are a strong candidate for a hair transplant'}
                    </h1>
                    <p className="text-sm text-gray-300 leading-relaxed">
                      This is how our algorithm predicts your results may look like with a hair transplant
                    </p>
                  </div>
                  
                  {/* Slider */}
                  {showBeforeAfterSlider && (
                    <div 
                      className="card-reveal"
                      onAnimationEnd={() => setBeforeAfterSliderComplete(true)}
                    >
                      <BeforeAfterSlider
                        beforeImage={imageDataUrl}
                        afterImage={generatedImageUrl}
                        beforeLabel="Before"
                        afterLabel="After"
                        initialPosition={50}
                        className="shadow-lg"
                      />
                      <p className="mt-3 text-xs text-gray-400 text-center leading-relaxed">
                        This is not a medical diagnosis. Match Medic uses advanced algorithms. Results may vary.
                      </p>
                    </div>
                  )}
                </div>
              )}
              
              {/* Fallback if no generated image - skip to cards */}
              {!hasComparisonImages && showBeforeAfterHeader && (
                <div 
                  className="card-reveal"
                  onAnimationEnd={() => {
                    setBeforeAfterHeaderComplete(true);
                    setBeforeAfterSliderComplete(true);
                  }}
                >
                  <h1 className="text-white text-2xl font-bold leading-tight mb-2">
                    {displayFirstName ? `${displayFirstName}, you are a strong candidate for a hair transplant` : 'You are a strong candidate for a hair transplant'}
                  </h1>
                  {generationError && (
                    <p className="text-sm text-red-400 text-center mb-4">
                      {generationError}
                    </p>
                  )}
                </div>
              )}
              
              {/* Analysis Cards Section */}
              {(beforeAfterSliderComplete || (!hasComparisonImages && showBeforeAfterHeader)) && (
                <div className="space-y-4 mt-8">
                  {/* Hair Transplant Candidate Score Card */}
                  {showScoreCard && (
                    <div 
                      className="card-reveal"
                      onAnimationEnd={() => setScoreCardComplete(true)}
                    >
                      <ColoredCard
                        title="Hair Transplant Candidate Score"
                        subtitle="How suitable are you for a hair transplant"
                        gradientColors={CARD_GRADIENTS.candidateScore}
                      >
                        <ProgressBar 
                          percent={candidateScorePercent} 
                          label={`${candidateScorePercent}%`}
                          minLabel="Low" 
                          maxLabel="High"
                          labelTextColor={CARD_GRADIENTS.candidateScore.from}
                          startAnimation={showScoreCard}
                        />
                      </ColoredCard>
                    </div>
                  )}
                  
                  {/* Expected Satisfaction Card */}
                  {showSatisfactionCard && (
                    <div 
                      className="card-reveal"
                      onAnimationEnd={() => setSatisfactionCardComplete(true)}
                    >
                      <ColoredCard
                        title="Expected Satisfaction"
                        subtitle="Based on 1000+ reviews matched to your attributes"
                        gradientColors={CARD_GRADIENTS.expectedSatisfaction}
                      >
                        <ProgressBar 
                          percent={expectedSatisfactionPercent} 
                          label={`${expectedSatisfactionPercent}%`}
                          minLabel="Low" 
                          maxLabel="High"
                          labelTextColor={CARD_GRADIENTS.expectedSatisfaction.from}
                          startAnimation={showSatisfactionCard}
                        />
                      </ColoredCard>
                    </div>
                  )}
                  
                  {/* WhatsApp Contact Card */}
                  {showWhatsAppCard && (
                    <div 
                      className="card-reveal"
                      onAnimationEnd={() => setWhatsAppCardComplete(true)}
                    >
                      <div 
                        className="rounded-2xl p-4 sm:p-5 shadow-lg"
                        style={{ background: 'linear-gradient(to bottom right, #BCBCBC, #EAEAEA)' }}
                      >
                        <div className="flex items-center justify-between gap-1">
                          {/* Left side: Title text */}
                          <p className="flex-1 text-base font-semibold text-gray-800 leading-snug">
                            Connect with our consultants right now
                          </p>
                          
                          {/* Right side: WhatsApp button */}
                          <a
                            href="https://wa.me/908503469160?text=Hi!%20I'm%20interested%20in%20your%20services."
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => {
                              e.stopPropagation();
                              // Track new chat button click
                              const clickEntry = {
                                slideName: 'before_after',
                                slideNumber: 11,
                                buttonType: 'whatsapp',
                                href: 'https://wa.me/908503469160',
                                timestamp: new Date().toISOString(),
                              };
                              if (onAutoSave) {
                                onAutoSave({ contactButtonClicks: [clickEntry] });
                              }
                              trackEvent('new_chat_button_clicked', {
                                button_type: 'whatsapp',
                                href: 'https://wa.me/908503469160',
                                source_type: 'onboarding',
                                source_detail: 'before_after_slide',
                                onboarding_id: sessionId || null,
                                slide_number: 11,
                              });
                              trackMetaLead({ content_name: 'before_after_whatsapp', content_category: 'onboarding_contact' });
                              trackTikTokContact('onboarding_before_after');
                            }}
                            className="flex-shrink-0 inline-flex items-center gap-2 rounded-full bg-mm_black px-4 py-2.5 transition hover:bg-gray-800"
                          >
                            <img 
                              src={WhatsAppLogo} 
                              alt="WhatsApp" 
                              className="h-5 w-5 object-contain" 
                            />
                            <span className="text-sm font-medium text-white whitespace-nowrap">
                              New Chat
                            </span>
                          </a>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* BenefitsDisplay - appears after all cards */}
                  {showBenefits && (
                    <div 
                      className="card-reveal"
                      onAnimationEnd={() => setBenefitsComplete(true)}
                    >
                      <BenefitsDisplay />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </SlideShell>
      
      {/* Save Report Popup */}
      <SaveReportPopup
        isOpen={showSaveReportPopup}
        onClose={() => setShowSaveReportPopup(false)}
        sessionId={sessionId}
        slideNumber={11}
        slideName="before_after"
        userName={userName}
        onSaveContact={(entry) => {
          if (onAutoSave) {
            onAutoSave({ saveContacts: [entry] });
          }
        }}
        onSuccess={async ({ method, value }) => {
          console.log('[SMS System] SaveReportPopup onSuccess triggered (BeforeAfter):', { method, value: value ? `${value.slice(0, 6)}...` : null });
          
          setIsReportSaved(true);
          setShowSaveHighlight(true);
          
          // Trigger SMS if phone method was used
          // NOTE: We use the ANALYSIS requestId (not generation) because the SMS link
          // goes to /view-report/{analysisRequestId}?tab=generation
          if (method === 'phone') {
            console.log('[SMS System] Phone method detected, checking for SMS credentials...');
            console.log('[SMS System] smsAnalysisRequestId:', smsAnalysisRequestId);
            console.log('[SMS System] smsPassword exists:', !!smsPassword);
            
            if (smsAnalysisRequestId && smsPassword) {
              try {
                console.log('[SMS System] Calling sendGenerationSMS with analysis requestId...');
                
                // Send SMS (non-blocking - fire and forget with tracking)
                const smsResult = await sendGenerationSMS({
                  targetNumber: value,
                  requestId: smsAnalysisRequestId,  // Using ANALYSIS requestId
                  password: smsPassword,
                  userName: userName || undefined,
                });
                
                console.log('[SMS System] sendGenerationSMS result:', smsResult);
                
                // Track the SMS trigger event
                trackEvent('sms_generation_report_triggered', {
                  onboarding_id: sessionId || null,
                  slide_number: 11,
                  slide_name: 'before_after',
                  target_number: value,
                  request_id: smsAnalysisRequestId,
                  user_name: userName || null,
                  sms_success: smsResult.success,
                  sms_message_sid: smsResult.messageSid || null,
                  sms_error: smsResult.error || null,
                  sms_duration: smsResult.duration || null,
                }).catch(console.error);
                
                // Sync SMS result to Firestore
                if (onAutoSave) {
                  onAutoSave({
                    smsGenerationTriggers: [{
                      slideNumber: 11,
                      slideName: 'before_after',
                      targetNumber: value,
                      requestId: smsAnalysisRequestId,
                      userName: userName || null,
                      timestamp: smsResult.timestamp,
                      success: smsResult.success,
                      messageSid: smsResult.messageSid || null,
                      error: smsResult.error || null,
                      duration: smsResult.duration || null,
                    }],
                  });
                }
              } catch (err) {
                console.error('[SMS System] Error in SMS flow:', err);
              }
            } else {
              console.warn('[SMS System] Missing SMS credentials - skipping SMS:', {
                hasAnalysisRequestId: !!smsAnalysisRequestId,
                hasPassword: !!smsPassword,
              });
              
              // Track missing credentials
              trackEvent('sms_generation_report_skipped', {
                onboarding_id: sessionId || null,
                slide_number: 11,
                reason: 'missing_credentials',
                has_request_id: !!smsAnalysisRequestId,
                has_password: !!smsPassword,
              }).catch(console.error);
            }
          } else if (method === 'email') {
            console.log('[AutoMail] Email method detected (BeforeAfter), checking for credentials...');

            if (smsAnalysisRequestId && smsPassword) {
              try {
                const emailResult = await sendGenerationEmail({
                  email: value,
                  requestId: smsAnalysisRequestId,
                  password: smsPassword,
                  userName: userName || undefined,
                  sessionId,
                });

                console.log('[AutoMail] sendGenerationEmail result:', emailResult);

                if (onAutoSave) {
                  onAutoSave({
                    emailGenerationTriggers: [{
                      slideNumber: 11,
                      slideName: 'before_after',
                      targetEmail: value,
                      requestId: smsAnalysisRequestId,
                      userName: userName || null,
                      timestamp: emailResult.timestamp,
                      success: emailResult.success,
                      messageId: emailResult.messageId || null,
                      followupsScheduled: emailResult.followupsScheduled ?? null,
                      error: emailResult.error || null,
                      duration: emailResult.duration || null,
                    }],
                  });
                }
              } catch (err) {
                console.error('[AutoMail] Error in email report flow (BeforeAfter):', err);
              }
            } else {
              console.warn('[AutoMail] Missing credentials - skipping report email (BeforeAfter):', {
                hasAnalysisRequestId: !!smsAnalysisRequestId,
                hasPassword: !!smsPassword,
              });
            }
          }
        }}
      />
    </div>
  );
}
